import React, { useState, useEffect, useCallback } from 'react';
import { useSupabase } from '../../../providers/SupabaseProvider';
import { useAuth } from '../../../providers/SupabaseProvider';
import { fetchSampleTypeInfo } from '../../../utils/sampleCollectionUtils';
import { validateSampleForEvaluation } from '../../../utils/sampleValidation';
import { Loader2, X, Calendar, Clock, FileCheck, AlertCircle, CheckCircle, XCircle, HelpCircle, Hash, RotateCcw } from 'lucide-react';

interface SampleReceiveResultModalProps {
  show: boolean;
  onClose: () => void;
  sampleId: string;
  onSampleUpdated: () => void;
}

interface SampleSubstance {
  id: string;
  substance_id: string;
  substance_name: string;
  category_name?: string | null;
  is_positive?: boolean | null;
}

const SampleReceiveResultModal: React.FC<SampleReceiveResultModalProps> = ({ 
  show, 
  onClose, 
  sampleId,
  onSampleUpdated
}) => {
  const supabase = useSupabase();
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sample, setSample] = useState<any | null>(null);
  const [sampleType, setSampleType] = useState<any | null>(null);
  const [resetting, setResetting] = useState(false);
  const [sampleSubstances, setSampleSubstances] = useState<SampleSubstance[]>([]);
  const [positiveSubstances, setPositiveSubstances] = useState<string[]>([]);
  const [formData, setFormData] = useState({
    received_date: new Date().toISOString().split('T')[0],
    received_time: new Date().toTimeString().slice(0, 5),
    result: '',
    internal_order_number: '',
    external_order_number: '',
    creatinine: "",
    examined_segment_alcohol: '',
    examined_segment_drugs: '',
    hair_length: '',
    hair_color: ''
  });

  const groupSubstancesByCategory = (substances: SampleSubstance[]) => {
    const grouped: Record<string, SampleSubstance[]> = {};
    substances.forEach((substance) => {
      const category = substance.category_name || 'Andere';
      if (!grouped[category]) {
        grouped[category] = [];
      }
      grouped[category].push(substance);
    });
    return grouped;
  };

  const loadSample = useCallback(async () => {
    if (!sampleId) return;

    try {
      setError(null);

      const { data, error } = await supabase
        .from('customer_samples')
        .select(`
          id,
          status,
          sample_type_id,
          sample_type_name,
          sample_data,
          internal_order_number,
          external_order_number,
          appointment_date,
          appointment_time,
          collection_date,
          collection_time,
          sent_date,
          sent_time,
          received_date,
          received_time,
          result,
          received_by,
          received_by_uuid,
          creatinine,
          hair_length,
          hair_color
        `)
        .eq('id', sampleId)
        .single();

      if (error) throw error;

      setSample(data);

      const creatinineValue =
        data?.creatinine ??
        (data?.sample_data && data.sample_data.creatinine !== undefined
          ? data.sample_data.creatinine
          : null);

      setFormData({
        received_date: data?.received_date || new Date().toISOString().split('T')[0],
        received_time: data?.received_time || new Date().toTimeString().slice(0, 5),
        result: data?.result || '',
        internal_order_number: data?.internal_order_number || '',
        external_order_number: data?.external_order_number || '',
        creatinine:
          creatinineValue !== null && creatinineValue !== undefined && creatinineValue !== ''
            ? String(creatinineValue)
            : '',
        examined_segment_alcohol: data?.sample_data?.examined_segment_alcohol || '',
        examined_segment_drugs: data?.sample_data?.examined_segment_drugs || '',
        hair_length:
          data?.sample_data?.hair_length !== null && data?.sample_data?.hair_length !== undefined
            ? String(data.sample_data.hair_length)
            : data?.hair_length !== null && data?.hair_length !== undefined
              ? String(data.hair_length)
            : '',
        hair_color: data?.sample_data?.hair_color || data?.hair_color || ''
      });

      const { data: substanceDetails, error: substanceError } = await supabase
        .from('sample_substance_details')
        .select('id, substance_id, substance_name, category_name, is_positive')
        .eq('sample_id', sampleId);

      if (substanceError) throw substanceError;
      const substances = (substanceDetails || []) as SampleSubstance[];
      setSampleSubstances(substances);
      setPositiveSubstances(
        substances.filter((substance) => substance.is_positive).map((substance) => substance.substance_id),
      );

      try {
        const typeInfo = await fetchSampleTypeInfo(supabase, sampleId);
        setSampleType(typeInfo);
      } catch (typeErr) {
        console.error('Error loading sample type:', typeErr);
        setError('Fehler beim Laden der Probenart');
      }
    } catch (err) {
      console.error('Error loading sample:', err);
      setError('Fehler beim Laden der Probe');
    }
  }, [sampleId, supabase]);

  useEffect(() => {
    if (show && sampleId) {
      loadSample();
    }
  }, [show, sampleId, loadSample]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      if (!sample) throw new Error('Keine Probe geladen');

      const isHairSample = (sampleType?.name || sample.sample_type_name) === 'Haar';
      const parsedHairLength =
        isHairSample && formData.hair_length.trim()
          ? parseFloat(formData.hair_length.replace(',', '.'))
          : null;
      const hairLengthValue =
        typeof parsedHairLength === 'number' && !Number.isNaN(parsedHairLength)
          ? parsedHairLength
          : null;
      const hairColorValue = isHairSample ? formData.hair_color.trim() : '';

      const missingFields = validateSampleForEvaluation({
        sample_type_name: sampleType?.name || sample.sample_type_name,
        appointment_date: sample.appointment_date,
        appointment_time: sample.appointment_time,
        collection_date: sample.collection_date,
        collection_time: sample.collection_time,
        sent_date: sample.sent_date,
        sent_time: sample.sent_time,
        received_date: formData.received_date,
        received_time: formData.received_time,
        sample_data: {
          ...(sample.sample_data ?? {}),
          ...(isHairSample
            ? {
                hair_length: hairLengthValue,
                hair_color: hairColorValue || null
              }
            : {})
        }
      });

      if (missingFields.length > 0) {
        setError('Fehlende Angaben: ' + missingFields.join(', '));
        setLoading(false);
        return;
      }
      // Parse creatinine value, handling both comma and period as decimal separators
      const creatinineValue = formData.creatinine 
        ? parseFloat(formData.creatinine.replace(',', '.')) 
        : null;

      if (!user) throw new Error('Benutzer nicht angemeldet');
      
      const examinedSegmentAlcohol = formData.examined_segment_alcohol.trim() || null;
      const examinedSegmentDrugs = formData.examined_segment_drugs.trim() || null;

      if (formData.result === 'Positiv' && positiveSubstances.length === 0) {
        setError('Bitte wählen Sie mindestens eine positive Substanz aus.');
        setLoading(false);
        return;
      }

      // Update the sample directly instead of using the utility function
      const baseSampleData = sample?.sample_data ? { ...sample.sample_data } : {};
      const updatedSampleData = {
        ...baseSampleData,
        creatinine: creatinineValue
      };

      if (isHairSample) {
        updatedSampleData.examined_segment_alcohol = examinedSegmentAlcohol;
        updatedSampleData.examined_segment_drugs = examinedSegmentDrugs;
        updatedSampleData.hair_length = hairLengthValue;
        updatedSampleData.hair_color = hairColorValue || null;
      }

      const { error } = await supabase
        .from('customer_samples')
        .update({
          status: 'received',
          received_date: formData.received_date,
          received_time: formData.received_time,
          result: formData.result,
          received_by: user.full_name || user.id || 'Unbekannt',
          received_by_uuid: user.id,
          internal_order_number: formData.internal_order_number || null,
          external_order_number: formData.external_order_number || null,
          creatinine: creatinineValue,
          // Haarwerte werden wie im Bearbeitungsdialog in Spalten und JSON synchron gehalten.
          ...(isHairSample
            ? {
                hair_length: hairLengthValue,
                hair_color: hairColorValue || null
              }
            : {}),
          sample_data: updatedSampleData
        })
        .eq('id', sampleId);

      if (error) throw error;

      const { error: resetPositiveError } = await supabase
        .from('customer_sample_substances')
        .update({ is_positive: false })
        .eq('sample_id', sampleId);

      if (resetPositiveError) throw resetPositiveError;

      if (formData.result === 'Positiv' && positiveSubstances.length > 0) {
        const { error: positiveError } = await supabase
          .from('customer_sample_substances')
          .update({ is_positive: true })
          .eq('sample_id', sampleId)
          .in('substance_id', positiveSubstances);

        if (positiveError) throw positiveError;
      }

      await loadSample();
      onSampleUpdated();
      onClose();
    } catch (err: any) {
      console.error('Error receiving sample result:', err);
      setError(err.message || 'Fehler beim Speichern des Befunds');
    } finally {
      setLoading(false);
    }
  };

  const handleResetStatus = async () => {
    if (!sample) return;

    const confirmReset = confirm('Möchten Sie den Befundstatus wirklich zurücksetzen?');
    if (!confirmReset) return;

    setResetting(true);
    setError(null);

    try {
      const updatedSampleData = sample.sample_data
        ? {
            ...sample.sample_data,
            creatinine: null,
            examined_segment_alcohol: null,
            examined_segment_drugs: null
          }
        : sample.sample_data;

      const { error } = await supabase
        .from('customer_samples')
        .update({
          status: 'sent',
          received_date: null,
          received_time: null,
          result: null,
          received_by: null,
          received_by_uuid: null,
          creatinine: null,
          internal_order_number: formData.internal_order_number || null,
          external_order_number: formData.external_order_number || null,
          sample_data: updatedSampleData ?? null
        })
        .eq('id', sampleId);

      if (error) throw error;

      const { error: positiveError } = await supabase
        .from('customer_sample_substances')
        .update({ is_positive: false })
        .eq('sample_id', sampleId);

      if (positiveError) throw positiveError;

      await loadSample();
      onSampleUpdated();
    } catch (err: any) {
      console.error('Error resetting sample result:', err);
      setError(err.message || 'Fehler beim Zurücksetzen des Befunds');
    } finally {
      setResetting(false);
    }
  };

  if (!show) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 p-4 sm:p-6 rounded-lg shadow-xl w-full max-w-md">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center">
            <FileCheck className="h-5 w-5 mr-2 text-purple-500" />
            Befund erhalten
          </h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-500 dark:hover:text-gray-300"
          >
            <X className="h-5 w-5" />
          </button>
        </div>
        
        {error && (
          <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/50 text-red-700 dark:text-red-300 rounded-md text-sm flex items-start gap-2">
            <AlertCircle className="h-5 w-5 flex-shrink-0 mt-0.5" />
            <span>{error}</span>
          </div>
        )}
        
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 flex items-center">
              <Calendar className="h-4 w-4 mr-1 text-gray-400" />
              Befunddatum
            </label>
            <input
              type="date"
              value={formData.received_date}
              onChange={(e) => setFormData({ ...formData, received_date: e.target.value })}
              className="w-full rounded-md border-gray-300 dark:border-gray-600 dark:bg-gray-700"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 flex items-center">
              <Clock className="h-4 w-4 mr-1 text-gray-400" />
              Befundzeit
            </label>
            <input
              type="time"
              value={formData.received_time}
              onChange={(e) => setFormData({ ...formData, received_time: e.target.value })}
              className="w-full rounded-md border-gray-300 dark:border-gray-600 dark:bg-gray-700"
            />
          </div>
          
          <div>
            <label htmlFor="external_order_number" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 flex items-center">
              <Hash className="h-4 w-4 mr-1 text-gray-400" />
              Externe Auftragsnummer (Labor) 
            </label>
            <input
              type="text"
              id="external_order_number"
              value={formData.external_order_number}
              onChange={(e) => setFormData({ ...formData, external_order_number: e.target.value })}
              className="w-full rounded-md border-gray-300 dark:border-gray-600 dark:bg-gray-700"
              placeholder="Labor-Referenznummer (optional)"
            />
          </div>

          {/* Show Creatinine field only if it's a urine sample */}
          {sampleType && (sampleType.name === 'Urin' || sampleType.name === 'Schnelltest') && (
            <div>
              <label htmlFor="creatinine" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Kreatininwert (g/l) Verwertungsbereich 0.2 - 3.5 (g/l)
              </label>
              <input
                type="text"
                id="creatinine"
                value={formData.creatinine}
                onChange={(e) => {
                  setFormData({ ...formData, creatinine: e.target.value });
                }}
                className="w-full rounded-md border-gray-300 dark:border-gray-600 dark:bg-gray-700"
                placeholder="z. B. 0,2 oder 0.2"
              />
            </div>
          )}

          {sampleType?.name === 'Haar' && (
            <div className="space-y-3">
              <div>
                <label htmlFor="hair_length" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Haarlänge (cm)
                </label>
                <input
                  type="text"
                  id="hair_length"
                  value={formData.hair_length}
                  onChange={(e) => setFormData({ ...formData, hair_length: e.target.value })}
                  className="w-full rounded-md border-gray-300 dark:border-gray-600 dark:bg-gray-700"
                  placeholder="z. B. 3,5"
                />
              </div>
              <div>
                <label htmlFor="hair_color" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Haarfarbe
                </label>
                <input
                  type="text"
                  id="hair_color"
                  value={formData.hair_color}
                  onChange={(e) => setFormData({ ...formData, hair_color: e.target.value })}
                  className="w-full rounded-md border-gray-300 dark:border-gray-600 dark:bg-gray-700"
                  placeholder="z. B. Braun"
                />
              </div>
              <div>
                <label htmlFor="examined_segment_alcohol" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Untersuchtes Segment Alkohol
                </label>
                <input
                  type="text"
                  id="examined_segment_alcohol"
                  value={formData.examined_segment_alcohol}
                  onChange={(e) => setFormData({ ...formData, examined_segment_alcohol: e.target.value })}
                  className="w-full rounded-md border-gray-300 dark:border-gray-600 dark:bg-gray-700"
                  placeholder="z. B. 0–3 cm"
                />
              </div>
              <div>
                <label htmlFor="examined_segment_drugs" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Untersuchtes Segment Drogen
                </label>
                <input
                  type="text"
                  id="examined_segment_drugs"
                  value={formData.examined_segment_drugs}
                  onChange={(e) => setFormData({ ...formData, examined_segment_drugs: e.target.value })}
                  className="w-full rounded-md border-gray-300 dark:border-gray-600 dark:bg-gray-700"
                  placeholder="z. B. 0–6 cm"
                />
              </div>
            </div>
          )}
          
          <div>
            <label htmlFor="result" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Ergebnis
              <span className="text-red-500 ml-1">*</span> 
            </label>
            <div className="grid grid-cols-3 gap-2 mt-2">
              <button
                type="button"
                onClick={() => setFormData({ ...formData, result: 'Negativ' })}
                className={`flex items-center justify-center px-3 py-2 rounded-md text-sm font-medium ${
                  formData.result === 'Negativ'
                    ? 'bg-green-100 text-green-800 border-2 border-green-500 dark:bg-green-900/50 dark:text-green-300 dark:border-green-600'
                    : 'bg-gray-100 text-gray-800 border border-gray-300 dark:bg-gray-800 dark:text-gray-300 dark:border-gray-700'
                }`}
              >
                <CheckCircle className="h-4 w-4 mr-2" />
                Negativ
              </button>
              <button
                type="button"
                onClick={() => setFormData({ ...formData, result: 'Positiv' })}
                className={`flex items-center justify-center px-3 py-2 rounded-md text-sm font-medium ${
                  formData.result === 'Positiv'
                    ? 'bg-red-100 text-red-800 border-2 border-red-500 dark:bg-red-900/50 dark:text-red-300 dark:border-red-600'
                    : 'bg-gray-100 text-gray-800 border border-gray-300 dark:bg-gray-800 dark:text-gray-300 dark:border-gray-700'
                }`}
              >
                <XCircle className="h-4 w-4 mr-2" />
                Positiv
              </button>
              <button
                type="button"
                onClick={() => setFormData({ ...formData, result: 'Nicht verwertbar' })}
                className={`flex items-center justify-center px-3 py-2 rounded-md text-sm font-medium ${
                  formData.result === 'Nicht verwertbar'
                    ? 'bg-yellow-100 text-yellow-800 border-2 border-yellow-500 dark:bg-yellow-900/50 dark:text-yellow-300 dark:border-yellow-600'
                    : 'bg-gray-100 text-gray-800 border border-gray-300 dark:bg-gray-800 dark:text-gray-300 dark:border-gray-700'
                }`}
              >
                <HelpCircle className="h-4 w-4 mr-2" />
                Nicht verwertbar
              </button>
            </div>
            {!formData.result && (
              <p className="mt-2 text-xs text-red-500 dark:text-red-400">
                Bitte wählen Sie ein Ergebnis aus
              </p>
            )}
          </div>

          {formData.result === 'Positiv' && (
            <div className="space-y-3">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                Positive Substanzen
                <span className="text-red-500 ml-1">*</span>
              </label>
              {sampleSubstances.length === 0 ? (
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Keine Substanzen für diese Probe hinterlegt.
                </p>
              ) : (
                <div className="space-y-3">
                  {Object.entries(groupSubstancesByCategory(sampleSubstances)).map(([category, substances]) => (
                    <div key={category} className="rounded-md border border-gray-200 dark:border-gray-700 p-3">
                      <p className="text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">{category}</p>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                        {substances.map((substance) => {
                          const isChecked = positiveSubstances.includes(substance.substance_id);
                          return (
                            <label
                              key={substance.substance_id}
                              className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300"
                            >
                              <input
                                type="checkbox"
                                checked={isChecked}
                                onChange={(event) => {
                                  setPositiveSubstances((prev) => {
                                    if (event.target.checked) {
                                      return Array.from(new Set([...prev, substance.substance_id]));
                                    }
                                    return prev.filter((id) => id !== substance.substance_id);
                                  });
                                }}
                                className="rounded border-gray-300 dark:border-gray-600"
                              />
                              <span>{substance.substance_name}</span>
                            </label>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </div>
              )}
              {positiveSubstances.length === 0 && (
                <p className="text-xs text-red-500 dark:text-red-400">
                  Bitte wählen Sie mindestens eine Substanz aus
                </p>
              )}
            </div>
          )}
          
          <div className="flex flex-col gap-3 pt-4 border-t border-gray-200 dark:border-gray-700 sm:flex-row sm:items-center sm:justify-between">
            {sample?.status === 'received' && (
              <button
                type="button"
                onClick={handleResetStatus}
                disabled={resetting || loading}
                className="inline-flex items-center justify-center px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-200 bg-gray-100 dark:bg-gray-700 rounded-md hover:bg-gray-200 dark:hover:bg-gray-600 disabled:opacity-50"
              >
                {resetting ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                    Wird zurückgesetzt...
                  </>
                ) : (
                  <>
                    <RotateCcw className="h-4 w-4 mr-2" />
                    Status zurücksetzen
                  </>
                )}
              </button>
            )}
            <div className="flex justify-end gap-3">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 rounded-md"
              >
                Abbrechen
              </button>
              <button
                type="submit"
                disabled={loading || !formData.result || (formData.result === 'Positiv' && positiveSubstances.length === 0)}
                className="px-4 py-2 text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 rounded-md disabled:opacity-50 flex items-center"
              >
                {loading ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                    Wird gespeichert...
                  </>
                ) : (
                  'Befund speichern'
                )}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default SampleReceiveResultModal;
